import { z } from 'zod';

import { LAYOUT_HEADER_TYPE } from '@widget/storyblok/contants';
import { APPOINTMENT_TYPE } from '@widget/api/types';

const sbComponentProps = {
  _uid: z.string(),
  _editable: z.string().nullish(),
};

const showUSPListSchema = z.union([
  z.literal('oldRegionalTrends'),
  z.literal('newRegionalTrends'),
  z.string().nullish(),
]);

const IconNameSchema = z
  .literal('')
  .or(z.literal('wald'))
  .or(z.literal('verkaufen'))
  .or(z.literal('total-flat-sq'))
  .or(z.literal('sonstige'))
  .or(z.literal('rooms-sq'))
  .or(z.literal('praxis'))
  .or(z.literal('pension'))
  .or(z.literal('partially-developed'))
  .or(z.literal('owning'))
  .or(z.literal('not-sure'))
  .or(z.literal('not-sure-1'))
  .or(z.literal('not-owning'))
  .or(z.literal('not-developed'))
  .or(z.literal('moved'))
  .or(z.literal('mehrfamilien-house'))
  .or(z.literal('market'))
  .or(z.literal('land-sq'))
  .or(z.literal('kaufen'))
  .or(z.literal('inherit'))
  .or(z.literal('industrial'))
  .or(z.literal('house-sq'))
  .or(z.literal('gescheftshaus'))
  .or(z.literal('fully-developed'))
  .or(z.literal('frame'))
  .or(z.literal('farmland'))
  .or(z.literal('eg'))
  .or(z.literal('divorce'))
  .or(z.literal('commercial'))
  .or(z.literal('best'))
  .or(z.literal('bauland'))
  .or(z.literal('baujahr'))
  .or(z.literal('basic'))
  .or(z.literal('average'))
  .or(z.literal('asap'))
  .or(z.literal('asap-1'))
  .or(z.literal('asap-2'))
  .or(z.literal('asap-3'))
  .or(z.literal('appartment'))
  .or(z.literal('appartment-house'))
  .or(z.literal('andere'))
  .or(z.literal('acting-on-behalf'))
  .or(z.literal('own-use'))
  .or(z.literal('half-rented'))
  .or(z.literal('rented'))
  .or(z.literal('free'))
  .or(z.literal('arrow-back'))
  .or(z.literal('caret-down'))
  .or(z.literal('caret-right'))
  .or(z.literal('star-circle-colored'))
  .or(z.literal('star-circle'))
  .or(z.literal('burger'))
  .or(z.literal('close'))
  .or(z.literal('cancel'))
  .or(z.literal('success'))
  .or(z.literal('location'))
  .or(z.literal('favorites'))
  .or(z.literal('list'))
  .or(z.literal('navigation'))
  .or(z.literal('square-dotted'))
  .or(z.literal('house-with-question-mark'))
  .or(z.literal('apartment'))
  .or(z.literal('two-stars'))
  .or(z.literal('one-star'))
  .or(z.literal('three-stars'))
  .or(z.literal('stall'))
  .or(z.literal('building'))
  .or(z.literal('tractor'))
  .or(z.literal('two-buildings'))
  .or(z.literal('house'))
  .or(z.literal('two-small-houses'))
  .or(z.literal('building-in-cart'))
  .or(z.literal('land'))
  .or(z.literal('multifamily-house'))
  .or(z.literal('crane'))
  .or(z.literal('building-under-construction'))
  .or(z.literal('penthouse'))
  .or(z.literal('building-with-question-mark'))
  .or(z.literal('phone'))
  .or(z.literal('townhouse'))
  .or(z.literal('two-family-house'))
  .or(z.literal('keys'))
  .or(z.literal('info-outlined'))
  .or(z.literal('slider-thumb'))
  .or(z.literal('calendar'))
  .or(z.literal('clock'))
  .or(z.literal('checkmark'))
  .or(z.literal('checkmarkColored'))
  .or(z.literal('chevronLeft'))
  .or(z.literal('chevronRight'))
  .or(z.literal('filter-icon'))
  .or(z.literal('map-fullscreen-icon'))
  .or(z.literal('SaleHorizon1Icon'))
  .or(z.literal('SaleHorizon4Icon'))
  .or(z.literal('OpenInNewFilled'))
  .or(z.literal('HandShakeIcon'))
  .or(z.literal('MarketTrendIcon'))
  .or(z.literal('SquareMeterIcon'));

const KeyValueSchema = z.array(
  z.object({
    key: z.string(),
    value: z.string(),
  }),
);

export const AssetSchema = z
  .object({
    id: z.number().nullish(),
    alt: z.string().nullish(),
    filename: z.string().nullish(),
  })
  .or(z.literal(''));

export const HeadingSchema = z.object({
  ...sbComponentProps,
  component: z.literal('Heading'),
  text: z.string(),
  variant: z
    .literal('')
    .or(z.literal('h1'))
    .or(z.literal('h2'))
    .or(z.literal('h3'))
    .or(z.literal('h4')),
});

const fontStyles = z.object({
  color: z.string().nullish(),
  fontFamily: z.string().nullish(),
  fontWeight: z.string().nullish(),
  fontSize: z.string().nullish(),
  lineHeight: z.string().nullish(),
  display: z
    .literal('')
    .or(z.literal('block'))
    .or(z.literal('inline'))
    .or(z.literal('flex'))
    .or(z.literal('inline-flex'))
    .or(z.literal('none'))
    .nullish(),
  textAlign: z
    .literal('')
    .or(z.literal('left'))
    .or(z.literal('right'))
    .or(z.literal('center'))
    .nullish(),
  variant: z.literal('').or(z.literal('b1')).or(z.literal('b2')).or(z.literal('grey')).nullish(),
});

export const TextSchema = z
  .object({
    ...sbComponentProps,
    component: z.literal('Text'),
    text: z.string(),
  })
  .and(fontStyles);

export const LinkSchema = z
  .object({
    ...sbComponentProps,
    component: z.literal('Link'),
    text: z.string().nullish(),
    openInNewWindow: z.boolean().nullish(),
    url: z.string().nullish(),
  })
  .and(fontStyles);

export const ImageSchema = z.object({
  ...sbComponentProps,
  component: z.literal('Image'),
  width: z.string().nullish(),
  maxWidth: z.string().nullish(),
  height: z.string().nullish(),
  maxHeight: z.string().nullish(),
  asset: z
    .object({
      id: z.number().nullish(),
      alt: z.string().nullish(),
      filename: z.string(),
    })
    .or(z.literal('')),
});

export const NumberInputFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('NumberInputField'),
  required: z.boolean(),
  label: z.string(),
  name: z.string().nullish(),
  suffix: z.string().nullish(),
  min: z.string().nullish(),
  max: z.string().nullish(),
  decimalPrecision: z.string().nullish(),
  useHelperButtons: z.boolean().nullish(),
  showTooltip: z.boolean().nullish(),
  tooltip: z.string().nullish(),
  helperButtonStep: z.string().nullish(),
});

export const TextInputFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('TextInputField'),
  required: z.boolean(),
  label: z.string().nullish(),
  name: z.string().nullish(),
  defaultValue: z.string().nullish(),
  showTooltip: z.boolean().nullish(),
  tooltip: z.string().nullish(),
});

export const PhoneNumberFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('PhoneNumberField'),
  required: z.boolean(),
  label: z.string().nullish(),
  name: z.string().nullish(),
  infoText: z.string().nullish(),
  showTooltip: z.boolean().nullish(),
  tooltip: z.string().nullish(),
  disabled: z.boolean().nullish(),
  additionalValidation: z.boolean().nullish(),
});

export const SwitchFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('SwitchField'),
  width: z.string().nullish(),
  name: z.string().nullish(),
  options: z.string().nullish(),
  defaultValue: z.string().nullish(),
  errorMessage: z.string().nullish(),
  variant: z.enum(['default', 'mini']).optional(),
  keepErrorSpace: z.boolean().optional(),
});

export const HiddenFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('HiddenField'),
  name: z.string().nullish(),
  type: z.literal('string').or(z.literal('number')).or(z.literal('boolean')).nullish(),
  value: z.string().nullish(),
});

export const EmailFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('EmailField'),
  required: z.boolean(),
  label: z.string().nullish(),
  name: z.string().nullish(),
  infoText: z.string().nullish(),
  showTooltip: z.boolean().nullish(),
  tooltip: z.string().nullish(),
});

export const CheckBoxFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('CheckBoxField'),
  required: z.boolean(),
  label: z.string().nullish(),
  name: z.string().nullish(),
});

export const SelectSchema = z.object({
  ...sbComponentProps,
  component: z.literal('Select'),
  label: z.string().nullish(),
  name: z.string(),
  required: z.boolean().nullish(),
  numeric: z.boolean().nullish(),
  options: z.array(
    z.object({
      ...sbComponentProps,
      label: z.string(),
      value: z.string(),
      component: z.literal('OptionSelect'),
    }),
  ),
  keepErrorSpace: z.boolean().optional(),
});

export const GridSizeSchema = z
  .literal('1')
  .or(z.literal('2'))
  .or(z.literal('3'))
  .or(z.literal('4'))
  .or(z.literal('5'))
  .or(z.literal('6'))
  .or(z.literal('7'))
  .or(z.literal('8'))
  .or(z.literal('9'))
  .or(z.literal('10'))
  .or(z.literal('11'))
  .or(z.literal('12'))
  .or(z.literal(''));

export const ZipCodeFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('ZipCodeField'),
  label: z.string(),
  name: z.string(),
  enableAutocomplete: z.boolean().nullish(),
  keepErrorSpace: z.boolean().optional(),
  coverageFieldName: z.string().nullish(),
});

export const AddressSuggestFieldSchema = z.object({
  ...sbComponentProps,
  component: z.literal('AddressSuggestField'),
  label: z.string(),
  name: z.string(),
  required: z.boolean(),
  cityFieldName: z.string(),
  streetFieldName: z.string(),
  zipCodeFieldName: z.string(),
  noHouseNumberError: z.string().optional(),
  requiredErrorText: z.string().optional(),
});

export const SpacingSchema = z
  .literal('0')
  .or(z.literal('1'))
  .or(z.literal('2'))
  .or(z.literal('3'))
  .or(z.literal('4'))
  .or(z.literal('5'))
  .or(z.literal('6'))
  .or(z.literal('7'))
  .or(z.literal('8'))
  .or(z.literal('9'))
  .or(z.literal('10'))
  .or(z.literal('12'))
  .or(z.literal('14'))
  .or(z.literal('16'))
  .or(z.literal('auto'))
  .or(z.literal(''))
  .nullish();

export const OptionSchema = z.object({
  ...sbComponentProps,
  component: z.literal('Option'),
  icon: IconNameSchema,
  image: AssetSchema.or(z.literal('')).nullish(),
  imageHover: AssetSchema.or(z.literal('')).nullish(),
  label: z.string(),
  value: z.string(),
  nextStep: z.string(),
});

export const OptionGroupSchema = z.object({
  ...sbComponentProps,
  component: z.literal('OptionGroup'),
  children: z.array(OptionSchema).nullish(),
  name: z.string(),
  submit: z.boolean(),
});

export const ActionButtonSchema = z.object({
  ...sbComponentProps,
  component: z.literal('ActionButton'),
  text: z.string().nullish(),
  subText: z.string().nullish(),
  submitTarget: z.enum(['lead', 'update-lead']).optional(),
});

export const CloseButtonSchema = z.object({
  ...sbComponentProps,
  component: z.literal('CloseButton'),
  text: z.string().nullish(),
});

export const DisclaimerSchema = z
  .object({
    ...sbComponentProps,
    component: z.literal('Disclaimer'),
    text: z.string().nullish(),
  })
  .and(fontStyles);

const BoxComponentSchema = z.literal('Box');

export const CardSchema = z.object({
  ...sbComponentProps,
  component: z.literal('Card'),
  asset: z.object({
    id: z.number().nullish(),
    alt: z.string().nullish(),
    filename: z.string(),
  }),
  ctaLink: z.string().nullish(),
  passQueryParams: z.string().nullish(),
});

export const FeedbackFormSchema = z.object({
  ...sbComponentProps,
  component: z.literal('FeedbackForm'),
  title: z.string().nullish(),
  inputLabel: z.string().nullish(),
  submitText: z.string().nullish(),
  successText: z.string().nullish(),
  otherFieldValue: z.string().nullish(),
  otherFieldLabel: z.string().nullish(),
  fields: KeyValueSchema.nullish(),
});

export const TrendOptionsSchema = z
  .union([
    z.literal('averageSqmPrice'),
    z.literal('averageSellingTime'),
    z.literal('propertiesSold'),
    z.literal('brokerCount'),
    z.literal('quarterSqmPriceTrend'),
  ])
  .nullish();

export const RegionalTrendSchema = z.object({
  ...sbComponentProps,
  component: z.literal('RegionalTrend'),
  trend: TrendOptionsSchema,
  icon: IconNameSchema.nullish(),
  description: z.string().nullish(),
});

export const RegionalTrendSectionSchema = z.object({
  ...sbComponentProps,
  component: z.literal('RegionalTrendSection'),
  children: z.array(RegionalTrendSchema),
});

const BoxChildrenPartialSchema = z.union([
  z.discriminatedUnion('component', [
    TextInputFieldSchema,
    PhoneNumberFieldSchema,
    SwitchFieldSchema,
    HiddenFieldSchema,
    EmailFieldSchema,
    CheckBoxFieldSchema,
    ActionButtonSchema,
    NumberInputFieldSchema,
    ImageSchema,
    ZipCodeFieldSchema,
    AddressSuggestFieldSchema,
    HeadingSchema,
    OptionGroupSchema,
    SelectSchema,
    CardSchema,
    FeedbackFormSchema,
    RegionalTrendSchema,
    RegionalTrendSectionSchema,
  ]),
  DisclaimerSchema,
  TextSchema,
]);

export const TextAlignSchema = z
  .literal('start')
  .or(z.literal('end'))
  .or(z.literal('left'))
  .or(z.literal('right'))
  .or(z.literal('center'))
  .or(z.literal('justify'))
  .or(z.literal('justify-all'))
  .or(z.literal('match-parent'))
  .or(z.literal(''))
  .nullish();

export const MaxWidthSchema = z.string().nullish();

export interface BoxSchema {
  _uid: z.infer<typeof sbComponentProps._uid>;
  _editable?: z.infer<typeof sbComponentProps._editable>;
  component: z.infer<typeof BoxComponentSchema>;
  mt?: z.infer<typeof SpacingSchema>;
  mb?: z.infer<typeof SpacingSchema>;
  ml?: z.infer<typeof SpacingSchema>;
  mr?: z.infer<typeof SpacingSchema>;
  children?: (z.infer<typeof BoxChildrenPartialSchema> | BoxSchema)[];
  textAlign?: z.infer<typeof TextAlignSchema>;
  maxWidth?: z.infer<typeof MaxWidthSchema>;
}

// cast to z.ZodSchema<BoxSchema>
export const BoxSchema: z.ZodSchema<BoxSchema> = z.lazy(() =>
  z.object({
    ...sbComponentProps,
    component: BoxComponentSchema,
    mt: SpacingSchema,
    mb: SpacingSchema,
    ml: SpacingSchema,
    mr: SpacingSchema,
    children: z.array(
      z.union([
        z.discriminatedUnion('component', [
          TextInputFieldSchema,
          PhoneNumberFieldSchema,
          SwitchFieldSchema,
          HiddenFieldSchema,
          EmailFieldSchema,
          CheckBoxFieldSchema,
          ActionButtonSchema,
          NumberInputFieldSchema,
          ImageSchema,
          ZipCodeFieldSchema,
          AddressSuggestFieldSchema,
          HeadingSchema,
          OptionGroupSchema,
          SelectSchema,
        ]),
        BoxSchema,
        DisclaimerSchema,
        TextSchema,
      ]),
    ),
    textAlign: TextAlignSchema,
    maxWidth: MaxWidthSchema,
  }),
);

export const GridItemSchema = z.object({
  ...sbComponentProps,
  component: z.literal('GridItem'),
  xs: GridSizeSchema,
  sm: GridSizeSchema,
  md: GridSizeSchema,
  lg: GridSizeSchema,
  xl: GridSizeSchema,
  customStyle: z.string().nullish(),
  orderXs: z.string().nullish(),
  orderSm: z.string().nullish(),
  orderMd: z.string().nullish(),
  orderLg: z.string().nullish(),
  orderXl: z.string().nullish(),
  hideUnderBreakpoint: z
    .literal('')
    .or(z.literal('sm'))
    .or(z.literal('md'))
    .or(z.literal('lg'))
    .or(z.literal('xl'))
    .nullish(),
  children: z.array(
    z.union([
      z.discriminatedUnion('component', [
        TextInputFieldSchema,
        PhoneNumberFieldSchema,
        SwitchFieldSchema,
        HiddenFieldSchema,
        EmailFieldSchema,
        CheckBoxFieldSchema,
        ActionButtonSchema,
        NumberInputFieldSchema,
        ImageSchema,
        ZipCodeFieldSchema,
        AddressSuggestFieldSchema,
        HeadingSchema,
        OptionGroupSchema,
        SelectSchema,
        CardSchema,
        FeedbackFormSchema,
        RegionalTrendSchema,
        RegionalTrendSectionSchema,
      ]),
      BoxSchema,
      DisclaimerSchema,
      TextSchema,
      LinkSchema,
    ]),
  ),
});

export const GridSchema = z.object({
  ...sbComponentProps,
  component: z.literal('Grid'),
  justify_content: z.string(),
  align_items: z.string(),
  children: z.array(GridItemSchema),
});

const QuestionLayoutChild = z.union([
  z.discriminatedUnion('component', [
    ImageSchema,
    GridSchema,
    ZipCodeFieldSchema,
    AddressSuggestFieldSchema,
    NumberInputFieldSchema,
    TextInputFieldSchema,
    PhoneNumberFieldSchema,
    SwitchFieldSchema,
    HiddenFieldSchema,
    EmailFieldSchema,
    CheckBoxFieldSchema,
    ActionButtonSchema,
    HeadingSchema,
    OptionGroupSchema,
    SelectSchema,
    CardSchema,
    FeedbackFormSchema,
    RegionalTrendSchema,
    RegionalTrendSectionSchema,
  ]),
  BoxSchema,
  DisclaimerSchema,
  TextSchema,
]);

export const IconWithTextSchema = z.object({
  ...sbComponentProps,
  component: z.literal('IconWithText'),
  iconType: IconNameSchema.nullish(),
  text: z.string().nullish(),
});

export const OrderedListSchema = z.object({
  ...sbComponentProps,
  component: z.literal('OrderedList'),
  title: z.string().nullish(),
  items: z
    .array(
      z.object({
        text: z.string().nullish(),
      }),
    )
    .nullish(),
});

export const QuestionLayoutSchema = z.object({
  ...sbComponentProps,
  component: z.literal('QuestionLayout'),
  title: z.string(),
  icon: AssetSchema.nullish(),
  description: z.string(),
  children: z.array(QuestionLayoutChild),
  back: z.boolean().nullish(),
  submit: z.boolean().nullish(),
  skip: z.boolean().nullish(),
  backButtonText: z.string().nullish(),
  closeButtonText: z.string().nullish(),
  submitButtonText: z.string().nullish(),
  skipButtonText: z.string().nullish(),
  maxW: z
    .literal('')
    .or(z.literal('sm'))
    .or(z.literal('md'))
    .or(z.literal('lg'))
    .or(z.literal('xl'))
    .nullish(),
  mode: z.literal('').or(z.literal('default')).or(z.literal('mini')).nullish(),
});

const sharedStepProps = {
  ...sbComponentProps,
  id: z.string(),
  nextStep: z.string().nullish(),
  renderWhen: KeyValueSchema.nullish(),
  hideLiveValuation: z.boolean().nullish(),
  hideUSP: z.boolean().nullish(),
  disableLocalizedUsp: z.boolean().nullish(),
  disableAutofocus: z.boolean().nullish(),
  hideFooter: z.boolean().nullish(),
  socialProofMessage: z.any().nullish(),
  uspLayout: z.literal('').or(z.literal('horizontal')).or(z.literal('vertical')).nullish(),
  hideHeaderText: z.boolean().nullish(),
  swapUspTextAndTrustIcons: z.boolean().nullish(),
  fullWidthLayout: z.boolean().nullish(),
  headerType: z
    .enum([
      LAYOUT_HEADER_TYPE.SOCIAL_PROOF_MESSAGE,
      LAYOUT_HEADER_TYPE.TRUST_ELEMENTS,
      LAYOUT_HEADER_TYPE.DEFAULT,
      LAYOUT_HEADER_TYPE.EMPTY,
    ])
    .nullish(),
  analyticsMap: KeyValueSchema.nullish(),
  showUSPList: showUSPListSchema.nullish(),
  finalStep: z.boolean().nullish(),
  showProgressBar: z.boolean().nullish(),
};

export const StepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('Step'),
  children: z.array(QuestionLayoutSchema),
});

export const BookBrokerAppointmentStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('BookBrokerAppointmentStep'),
  children: z.array(QuestionLayoutSchema),
  name: z.string().nullish(),
  type: z.union([z.literal(APPOINTMENT_TYPE.CALL), z.literal(APPOINTMENT_TYPE.ON_SITE)]).nullish(),
  submitButtonLabel: z.string().nullish(),
  phoneInputLabel: z.string().nullish(),
  onSiteCheckboxLabel: z.string().nullish(),
  loadingText: z.string().nullish(),
});

export const BookSalesAppointmentStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('BookSalesAppointmentStep'),
  children: z.array(QuestionLayoutSchema),
  name: z.string().nullish(),
  submitButtonLabel: z.string().nullish(),
  loadingText: z.string().nullish(),
});

export const BookBrokerAppointmentChoiceStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('BookBrokerAppointmentChoiceStep'),
  children: z.array(QuestionLayoutSchema),
  name: z.string().nullish(),
  callButtonLabel: z.string().nullish(),
  callButtonHighlightLabel: z.string().nullish(),
  callButtonInfo: z.string().nullish(),
  onsiteButtonLabel: z.string().nullish(),
  onsiteButtonHighlightLabel: z.string().nullish(),
  onsiteButtonInfo: z.string().nullish(),
  isFirstBrokerBookingStep: z.boolean().nullish(),
  hideOnSite: KeyValueSchema.nullish(),
  loadingText: z.string().nullish(),
  SlotsAvailableFlagFieldName: z.string().nullish(),
});

export const BookBrokerAppointmentOnSiteStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('BookBrokerAppointmentOnSiteStep'),
  children: z.array(QuestionLayoutSchema),
  checkBoxText: z.string().nullish(),
  checkBoxTextCall: z.string().nullish(),
  ctaText: z.string().nullish(),
  phoneErrorText: z.string().nullish(),
  addressLabel: z.string().nullish(),
  addressErrorsMap: KeyValueSchema.nullish(),
});

export const AddressMultipleFieldsStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('AddressMultipleFieldsStep'),
  children: z.array(QuestionLayoutSchema),
  checkBoxText: z.string().nullish(),
  checkBoxTextCall: z.string().nullish(),
  ctaText: z.string().nullish(),
  phoneErrorText: z.string().nullish(),
  streetLabel: z.string().nullish(),
  numberLabel: z.string().nullish(),
  addressErrorsMap: KeyValueSchema.nullish(),
  disableValidation: z.boolean().nullish(),
});

export const BookBrokerAppointmentThankYouPageSchema = z.object({
  ...sharedStepProps,
  component: z.literal('BookBrokerAppointmentThankYouPage'),
  children: z.array(QuestionLayoutSchema),
  title: z.string().nullish(),
  description: z.string().nullish(),
  AddCalendarEventButton: z
    .array(
      z.object({
        skip: z.boolean(),
        text: z.string(),
        titleCall: z.string(),
        notesCall: z.string(),
        titleOnSite: z.string(),
        notesOnSite: z.string(),
      }),
    )
    .nullish(),
});

export const BookSalesAppointmentThankYouPageSchema = z.object({
  ...sharedStepProps,
  component: z.literal('BookSalesAppointmentThankYouPage'),
  children: z.array(QuestionLayoutSchema),
  title: z.string().nullish(),
  description: z.string().nullish(),
  AddCalendarEventButton: z
    .array(
      z.object({
        skip: z.boolean(),
        text: z.string(),
        titleCall: z.string(),
        notesCall: z.string(),
        titleOnSite: z.string(),
        notesOnSite: z.string(),
      }),
    )
    .nullish(),
});

export const ValuationStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('ValuationStep'),
  icon: AssetSchema.nullish(),
  title: z.string(),
  children: z.array(ImageSchema),
  closeButtonText: z.string().nullish(),
  loadingText: z.string().nullish(),
});

export const QuestionStepSchema = z.object({
  ...sharedStepProps,
  back: z.boolean().nullish(),
  includeGuidanceModal: z.boolean().nullish(),
  component: z.literal('QuestionStep'),
  icon: AssetSchema.nullish(),
  name: z.string(),
  title: z.string(),
  modalCta: z.string().nullish(),
  modalTitle: z.string().nullish(),
  modalGridSize: z.string().nullish(),
  guidances: z
    .array(
      z.object({
        title: z.string().nullish(),
        description: z.string().nullish(),
        helperText: z.string().nullish(),
        image: AssetSchema.nullish(),
      }),
    )
    .nullish(),
  gridSize: z.string().nullish(),
  valueType: z.string().nullish(),
  hiddenFields: z.array(HiddenFieldSchema).nullish(),
  options: z.array(
    z.object({
      ...sbComponentProps,
      icon: IconNameSchema,
      image: AssetSchema.or(z.literal('')).nullish(),
      imageHover: AssetSchema.or(z.literal('')).nullish(),
      label: z.string(),
      value: z.string(),
      nextStep: z.string(),
      component: z.literal('QuestionStepOption'),
      redirectUrl: z.string().nullish(),
    }),
  ),
  subTitle: z.string(),
  description: z.string(),
  backButtonText: z.string().nullish(),
  closeButtonText: z.string().nullish(),
  submitButtonText: z.string().nullish(),
  skipButtonText: z.string().nullish(),
  showUSPList: showUSPListSchema.nullish(),
});

export const ContactStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('ContactStep'),
  title: z.string().nullish(),
  icon: AssetSchema.nullish(),
  description: z.string().nullish(),
  firstNameLabel: z.string().nullish(),
  lastNameLabel: z.string().nullish(),
  phoneLabel: z.string().nullish(),
  phoneTooltip: z.string().nullish(),
  emailLabel: z.string().nullish(),
  emailTooltip: z.string().nullish(),
  buttonText: z.string().nullish(),
  buttonSubText: z.string().nullish(),
  closeButtonText: z.string().nullish(),
  disclaimerText: z.any().nullish(),
  newsletterText: z.string().nullish(),
  isNewsletterOptInShown: z.boolean().optional(),
  showUSPList: showUSPListSchema.nullish(),
});

export const ContactStepWithSliderSchema = z.object({
  ...sharedStepProps,
  component: z.literal('ContactStepWithSlider'),
  title: z.string().nullish(),
  icon: AssetSchema.nullish(),
  description: z.string().nullish(),
  firstNameLabel: z.string().nullish(),
  lastNameLabel: z.string().nullish(),
  phoneLabel: z.string().nullish(),
  phoneTooltip: z.string().nullish(),
  emailLabel: z.string().nullish(),
  emailTooltip: z.string().nullish(),
  buttonText: z.string().nullish(),
  buttonSubText: z.string().nullish(),
  closeButtonText: z.string().nullish(),
  disclaimerText: z.any().nullish(),
  newsletterText: z.string().nullish(),
  isNewsletterOptInShown: z.boolean().optional(),
  hideHomeIcon: z.boolean().nullish(),
  additionalCheckboxes: z.array(BoxSchema).nullish(),
  showUSPList: showUSPListSchema.nullish(),
});

export const ThankYouStepSchema = z.object({
  ...sharedStepProps,
  component: z.literal('ThankYouStep'),
  closeWidget: z.boolean().nullish(),
  bookCall: z.boolean().nullish(),
  heading: z.string().nullish(),
  title: z.string().nullish(),
  icon: AssetSchema.nullish(),
  description: z.string().nullish(),
  buttonText: z.string().nullish(),
  closeButtonText: z.string().nullish(),
  bookCallButtonText: z.string().nullish(),
  dataAcquiredText: z.string().nullish(),
  loadingText: z.string().nullish(),
  redirectionAlertText: z.string().nullish(),
  resetButtonText: z.string().nullish(),
  valuationText: z.string().nullish(),
  propertySearchLink: z.string().nullish(),
  redirectCustomLink: z.array(LinkSchema).nullish(),
  enableAutomaticRedirectionToPropertySearch: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .nullish(),
  PhoneLink: z
    .array(
      z.object({
        ...sbComponentProps,
        phoneCtaTel: z.string().nullish(),
        phoneCtaText: z.string().nullish(),
        phoneCtaTarget: z.string().nullish(),
        component: z.literal('PhoneLink'),
      }),
    )
    .nullish(),
  userAlreadyHasAppointmentErrorText: z.string().nullish(),
  children: z.array(QuestionLayoutChild).nullish(),
});

export const ThankYouStepV2Schema = z.object({
  ...sharedStepProps,
  component: z.literal('ThankYouStepV2'),
  heading: z.string().nullish(),
  buttonText: z.string().nullish(),
  closeButtonText: z.string().nullish(),
  propertySearchLink: z.string().nullish(),
  enableAutomaticRedirectionToPropertySearch: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .nullish(),
  children: z.array(QuestionLayoutChild).nullish(),
});

export const AnyStepSchema = z.discriminatedUnion('component', [
  QuestionStepSchema,
  ContactStepSchema,
  ContactStepWithSliderSchema,
  ThankYouStepSchema,
  ValuationStepSchema,
  StepSchema,
  BookBrokerAppointmentStepSchema,
  BookSalesAppointmentStepSchema,
  BookBrokerAppointmentChoiceStepSchema,
  BookBrokerAppointmentOnSiteStepSchema,
  BookBrokerAppointmentThankYouPageSchema,
  BookSalesAppointmentThankYouPageSchema,
  AddressMultipleFieldsStepSchema,
]);

export const WidgetItemSchema = z
  .object({
    content: z.object({
      ...sbComponentProps,
      component: z.literal('Widget'),
      steps: z.array(AnyStepSchema).nullish(),
      maxNumberOfSteps: z.string().nullish(),
      logo: AssetSchema.nullish(),
      contactPerson: AssetSchema.nullish(),
      marketingHeaderText: z.any().nullish(),
      closeButtonText: z.string().nullish(),
      enableReset: z.boolean().nullish(),
      cancelIcon: AssetSchema.nullish(),
      showLiveValuation: z.boolean().nullish(),
      footer: z
        .array(
          z.object({
            icon: IconNameSchema,
            label: z.string(),
          }),
        )
        .nullish(),
      analyticsMap: KeyValueSchema.nullish(),
      loadingText: z.string().nullish(),
      // flags
      personalization: z.boolean().nullish(),
      agentCityPrefix: z.string().nullish(),
      showLocalizedUSP: z.boolean().nullish(),
      exludeLocalizedUspForMarkets: z.array(z.string()).nullish(),
      isSearchProfile: z.boolean().nullish(),
      enableExitPopup: z.boolean().nullish(),
      localizedUspLabel: z.string().nullish(),
      closeModalTitle: z.string().nullish(),
      closeModalSubtitle: z.string().or(z.record(z.string(), z.unknown())).nullish(),
      closeModalCloseModalText: z.string().nullish(),
      closeModalCloseWidgetText: z.string().nullish(),
      trustIconsList: z
        .array(
          z.object({
            imageAlt: z.string().nullish(),
            imageHeight: z.string().nullish(),
            imageWidth: z.string().nullish(),
            image: z
              .object({
                id: z.string().nullish(),
                url: z.string().nullish(),
                cached_url: z.string().nullish(),
              })
              .nullish(),
            link: z
              .object({
                id: z.string().nullish(),
                url: z.string().nullish(),
                cached_url: z.string().nullish(),
              })
              .nullish(),
          }),
        )
        .nullish(),
    }),
  })
  .strip();

export type WidgetItemType = z.infer<typeof WidgetItemSchema>;
